import { linkWorkoutItems } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, Button } from "ui";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { useWorkoutItemsDragAndDropManager } from "src/workouts/WorkoutItemsDragAndDropManagerContext";

import { useMinimapDragAndDropManager } from "./MinimapDragAndDropManager";

interface MinimapLinkZoneProps {
  aboveItemId: string;
  belowItemId: string;
  level: number;
}

export const MinimapLinkZone = observer(function MinimapLinkZone(
  props: MinimapLinkZoneProps,
) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const manager = useWorkoutItemsDragAndDropManager();
  const minimapManager = useMinimapDragAndDropManager();
  const { startSaving } = useAuthenticatedNavigationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const canLink = workoutEditStore.canMergeWithNext(props.aboveItemId);

  if (!canLink) {
    return null;
  }

  async function handleLink() {
    setIsLoading(true);
    const stop = startSaving();

    try {
      const response = await linkWorkoutItems({
        path: { workoutId: workoutEditStore.workout.id },
        body: { itemId: props.aboveItemId, withItemId: props.belowItemId },
      });

      const data = response.data.data;

      const newGroupIds = data.operations.flatMap((op) => {
        return op.operation === "create" && op.data.type === "GROUP"
          ? [op.data.id]
          : [];
      });
      manager.openAccordionItems(newGroupIds);

      for (const id of newGroupIds) {
        minimapManager.expandItem(id);
      }

      workoutEditStore.applyWorkoutItemChanges(data.operations);

      stop();
    } catch {
      stop("error");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div
      className={cn(
        "absolute -top-[4px] left-0 right-0 h-2 -translate-y-1/2 flex items-center justify-center z-10",
        "transition-opacity duration-150",
      )}
      style={{ paddingLeft: `${props.level * 12}px` }}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <Button
        variant="ghost"
        size="icon"
        disabled={isLoading}
        onClick={handleLink}
        className={cn(
          "h-3.5 w-3.5 rounded-full z-10 transition-all",
          "bg-amber-500 hover:bg-amber-600",
          "text-white hover:text-white",
          isHovered ? "opacity-100 scale-100" : "opacity-0 scale-75",
        )}
        title={t("editWorkout.link.tooltip")}
      >
        <Icon name="tabler-link" className="!h-2.5 !w-2.5" strokeWidth={2.5} />
      </Button>
    </div>
  );
});
